import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Activity,
    AlertCircle,
    ArrowDownRight,
    ArrowUpRight,
    BarChart3,
    Bell,
    Calculator,
    CheckCircle2,
    Clock,
    CreditCard,
    DollarSign,
    Download,
    FileText,
    Plus,
    Receipt,
    RefreshCw,
    Target,
    TrendingUp,
    Users,
    Zap
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Import enhanced billing components
import BillingCalculator from '../components/billing/BillingCalculator';
import BillingReports from '../components/billing/BillingReports';
import InvoiceGeneration from '../components/billing/InvoiceGeneration';
import PaymentTracking from '../components/billing/PaymentTracking';

interface DashboardMetrics {
  totalRevenue: number;
  pendingAmount: number;
  overdueAmount: number;
  collectionRate: number;
  invoiceCount: number;
  paidInvoices: number;
  avgInvoiceValue: number;
  profitMargin: number;
  monthlyGrowth: number;
  activeCustomers: number;
}

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: any;
  color: string;
  action: () => void;
}

interface RecentActivity {
  id: string;
  type: 'invoice' | 'payment' | 'calculation' | 'report';
  title: string;
  description: string;
  amount?: number;
  timestamp: Date;
  status: 'success' | 'pending' | 'warning' | 'error';
}

const BILLING_TABS = ['overview', 'calculator', 'invoice', 'payments', 'reports'] as const;
type BillingTab = (typeof BILLING_TABS)[number];

const BillingManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const resolveTab = useCallback((value: string | null): BillingTab => {
    if (value && BILLING_TABS.includes(value as BillingTab)) {
      return value as BillingTab;
    }
    return 'overview';
  }, []);
  const [activeTab, setActiveTab] = useState<BillingTab>(() => resolveTab(searchParams.get('tab')));
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalRevenue: 2850000,
    pendingAmount: 485000,
    overdueAmount: 125000,
    collectionRate: 88.3,
    invoiceCount: 145,
    paidInvoices: 128,
    avgInvoiceValue: 19655,
    profitMargin: 33.7,
    monthlyGrowth: 12.5,
    activeCustomers: 45
  });

  const [recentActivities] = useState<RecentActivity[]>([
    {
      id: '1',
      type: 'payment',
      title: 'Payment Received',
      description: 'TechCorp India - Invoice SI-001234',
      amount: 15000,
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      status: 'success'
    },
    {
      id: '2',
      type: 'invoice',
      title: 'Invoice Generated',
      description: 'SI-001240 for Mumbai Fashion House',
      amount: 25000,
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      status: 'success'
    },
    {
      id: '3',
      type: 'calculation',
      title: 'Billing Calculated',
      description: 'Express delivery - 12.5 KG to US',
      amount: 8500,
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
      status: 'success'
    },
    {
      id: '4',
      type: 'invoice',
      title: 'Overdue Invoice',
      description: 'PI-001001 payment overdue',
      amount: 8000,
      timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000),
      status: 'warning'
    }
  ]);

  const syncTabParam = useCallback(
    (tab: BillingTab) => {
      const nextParams = new URLSearchParams(searchParams);
      if (tab === 'overview') {
        nextParams.delete('tab');
      } else {
        nextParams.set('tab', tab);
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleTabChange = useCallback(
    (value: string) => {
      const nextTab = resolveTab(value);
      setActiveTab(nextTab);
      syncTabParam(nextTab);
    },
    [resolveTab, syncTabParam],
  );

  const quickActions: QuickAction[] = useMemo(
    () => [
      {
        id: 'calculate',
        title: 'Quick Calculate',
        description: 'Calculate billing for new shipments',
        icon: Calculator,
        color: 'bg-blue-500',
        action: () => handleTabChange('calculator')
      },
      {
        id: 'invoice',
        title: 'Generate Invoice',
        description: 'Create invoices for pending AWBs',
        icon: FileText,
        color: 'bg-green-500',
        action: () => handleTabChange('invoice')
      },
      {
        id: 'payment',
        title: 'Record Payment',
        description: 'Track and record new payments',
        icon: CreditCard,
        color: 'bg-purple-500',
        action: () => handleTabChange('payments')
      },
      {
        id: 'reports',
        title: 'View Reports',
        description: 'Access detailed analytics',
        icon: BarChart3,
        color: 'bg-orange-500',
        action: () => handleTabChange('reports')
      }
    ],
    [handleTabChange],
  );

  const getGrowthIcon = (growth: number) => {
    if (growth > 0) return <ArrowUpRight className="h-4 w-4 text-green-500" />;
    if (growth < 0) return <ArrowDownRight className="h-4 w-4 text-red-500" />;
    return <Activity className="h-4 w-4 text-gray-500" />;
  };

  const getGrowthColor = (growth: number) => {
    if (growth > 0) return 'text-green-600';
    if (growth < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'payment': return <CreditCard className="h-4 w-4" />;
      case 'invoice': return <FileText className="h-4 w-4" />;
      case 'calculation': return <Calculator className="h-4 w-4" />;
      case 'report': return <BarChart3 className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getActivityColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      default: return 'text-blue-600 bg-blue-100';
    }
  };

  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  useEffect(() => {
    const nextTab = resolveTab(searchParams.get('tab'));
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, resolveTab, searchParams]);

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Billing Management</h1>
          <p className="text-muted-foreground">
            Comprehensive billing operations and financial insights
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:w-auto">
          <Button variant="outline" size="sm" className="w-full sm:w-auto">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" className="w-full sm:w-auto">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button size="sm" className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />
            New Invoice
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="flex h-auto w-full flex-wrap gap-2 md:grid md:h-10 md:grid-cols-5 md:gap-0">
          <TabsTrigger value="overview" className="flex-1 min-w-[140px] justify-start gap-2 md:flex-none md:min-w-0 md:justify-center">
            <Target className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="calculator" className="flex-1 min-w-[140px] justify-start gap-2 md:flex-none md:min-w-0 md:justify-center">
            <Calculator className="h-4 w-4" />
            Calculator
          </TabsTrigger>
          <TabsTrigger value="invoice" className="flex-1 min-w-[140px] justify-start gap-2 md:flex-none md:min-w-0 md:justify-center">
            <FileText className="h-4 w-4" />
            Invoicing
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex-1 min-w-[140px] justify-start gap-2 md:flex-none md:min-w-0 md:justify-center">
            <CreditCard className="h-4 w-4" />
            Payments
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex-1 min-w-[140px] justify-start gap-2 md:flex-none md:min-w-0 md:justify-center">
            <BarChart3 className="h-4 w-4" />
            Reports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Key Metrics Dashboard */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">₹{metrics.totalRevenue.toLocaleString()}</div>
                <div className="flex items-center gap-2 mt-2">
                  {getGrowthIcon(metrics.monthlyGrowth)}
                  <p className={`text-xs font-medium ${getGrowthColor(metrics.monthlyGrowth)}`}>
                    +{metrics.monthlyGrowth}% from last month
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Collection Rate</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics.collectionRate.toFixed(1)}%</div>
                <div className="mt-2">
                  <Progress value={metrics.collectionRate} className="h-2" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {metrics.paidInvoices} of {metrics.invoiceCount} invoices
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Outstanding</CardTitle>
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">₹{metrics.pendingAmount.toLocaleString()}</div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="destructive" className="text-xs">
                    ₹{metrics.overdueAmount.toLocaleString()} Overdue
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Profit Margin</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics.profitMargin.toFixed(1)}%</div>
                <div className="mt-2">
                  <Progress value={metrics.profitMargin} className="h-2" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Target: 35%
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* Quick Actions */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Quick Actions
                </CardTitle>
                <CardDescription>
                  Common billing operations and shortcuts
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {quickActions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={action.id}
                        type="button"
                        onClick={action.action}
                        className="flex w-full items-center gap-4 rounded-lg border p-4 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <div className={`p-3 rounded-lg ${action.color} text-white`}>
                          <Icon className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{action.title}</h4>
                          <p className="text-sm text-muted-foreground">{action.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Recent Activity
                </CardTitle>
                <CardDescription>Latest billing operations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentActivities.map((activity) => (
                    <div key={activity.id} className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${getActivityColor(activity.status)}`}>
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{activity.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {activity.description}
                        </p>
                        <div className="flex items-center justify-between mt-1">
                          {activity.amount && (
                            <span className="text-xs font-medium">
                              ₹{activity.amount.toLocaleString()}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {formatTimeAgo(activity.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Performance Overview */}
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Monthly Performance
                </CardTitle>
                <CardDescription>Revenue and collection trends</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 text-center sm:grid-cols-3">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">₹28.5L</div>
                      <div className="text-xs text-blue-700">This Month</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">₹25.3L</div>
                      <div className="text-xs text-green-700">Last Month</div>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">+12.5%</div>
                      <div className="text-xs text-purple-700">Growth</div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>Revenue Target</span>
                      <span className="font-medium">85% achieved</span>
                    </div>
                    <Progress value={85} className="h-2" />

                    <div className="flex justify-between text-sm">
                      <span>Collection Target</span>
                      <span className="font-medium">92% achieved</span>
                    </div>
                    <Progress value={92} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Customer Insights
                </CardTitle>
                <CardDescription>Top performing customer segments</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Active Customers</div>
                      <div className="text-sm text-muted-foreground">This month</div>
                    </div>
                    <div className="text-2xl font-bold">{metrics.activeCustomers}</div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm">Enterprise</span>
                      <span className="font-medium">60%</span>
                    </div>
                    <Progress value={60} className="h-2" />

                    <div className="flex justify-between">
                      <span className="text-sm">SME</span>
                      <span className="font-medium">30%</span>
                    </div>
                    <Progress value={30} className="h-2" />

                    <div className="flex justify-between">
                      <span className="text-sm">Individual</span>
                      <span className="font-medium">10%</span>
                    </div>
                    <Progress value={10} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alerts & Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Alerts & Notifications
              </CardTitle>
              <CardDescription>Important billing notifications requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <div>
                    <div className="font-medium text-red-900">5 Overdue Invoices</div>
                    <div className="text-sm text-red-700">₹{metrics.overdueAmount.toLocaleString()} pending</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  <div>
                    <div className="font-medium text-yellow-900">12 Due Soon</div>
                    <div className="text-sm text-yellow-700">Within next 7 days</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <Receipt className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-blue-900">28 Ready to Invoice</div>
                    <div className="text-sm text-blue-700">AWBs pending billing</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calculator">
          <BillingCalculator />
        </TabsContent>

        <TabsContent value="invoice">
          <InvoiceGeneration />
        </TabsContent>

        <TabsContent value="payments">
          <PaymentTracking />
        </TabsContent>

        <TabsContent value="reports">
          <BillingReports />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BillingManagement;
